/**
 * Coups de pouce — Attendu 1122 « Reconnaître un parallélogramme à l'aide de
 * sa définition ou d'une propriété caractéristique grâce aux codages »
 * (automatisme 4e).
 *
 * conclure_qcm_unique, conclure_relier, retrouver_qcm_unique, vf_tableau_vf :
 * entrée PARTAGÉE « lire le codage » — la définition et les trois propriétés
 * caractéristiques qui suffisent, ce qui ne suffit pas, et la doctrine « seuls
 * les codages font foi, l'allure ne prouve rien ». Exemple : EFGH codé
 * (EF) // (HG) et EF = HG → parallélogramme (deux côtés opposés parallèles et
 * de même longueur) ; avec le parallélisme seul, on ne pourrait pas conclure.
 *
 * rappel_tableau_vf : pur rappel, pas d'entrée (doctrine 2026-07-31).
 * Lettres E, F, G, H hors du générateur (A, B, C, D, O).
 */

const E = [48, 168]; const F = [178, 168]; const G = [214, 106]; const H = [84, 106];
const SEG = (a, b, couleur = '#1e293b', largeur = 1.8) => `<line x1="${a[0]}" y1="${a[1]}" x2="${b[0]}" y2="${b[1]}" stroke="${couleur}" stroke-width="${largeur}"/>`;
const PT = (p, label, dx, dy) => `<circle cx="${p[0]}" cy="${p[1]}" r="2.6" fill="#1e293b"/><text x="${p[0] + dx}" y="${p[1] + dy}" font-family="system-ui,sans-serif" font-size="12.5" font-weight="800" fill="#1e293b">${label}</text>`;
/** Trait d'égalité au milieu de [a b], perpendiculaire au côté. */
const TICK = (a, b, couleur = '#1e293b') => {
    const mx = (a[0] + b[0]) / 2; const my = (a[1] + b[1]) / 2; const dx = b[0] - a[0]; const dy = b[1] - a[1]; const n = Math.hypot(dx, dy); const nx = -dy / n * 6; const ny = dx / n * 6;
    return `<line x1="${(mx + nx).toFixed(1)}" y1="${(my + ny).toFixed(1)}" x2="${(mx - nx).toFixed(1)}" y2="${(my - ny).toFixed(1)}" stroke="${couleur}" stroke-width="2.2"/>`;
};
const FIGURE = `<polygon points="${E.join(',')} ${F.join(',')} ${G.join(',')} ${H.join(',')}" fill="#dbeafe" fill-opacity="0.5" stroke="#1e293b" stroke-width="1.8"/>`
    + PT(E, 'E', -13, 14) + PT(F, 'F', 5, 14) + PT(G, 'G', 5, -6) + PT(H, 'H', -13, -6);
const T = (x, y, s, couleur, poids = 700, taille = 11) => `<text x="${x}" y="${y}" font-size="${taille}" font-weight="${poids}" fill="${couleur}">${s}</text>`;

const SVG_CODAGE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 330" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Question : d'après le codage, EFGH est-il un parallélogramme ? En 3 étapes à faire défiler avec les flèches. Étape 1 : on lit le codage, rien d'autre : un trait sur [EF] et un trait sur [HG], donc EF = HG ; et la mention (EF) // (HG). Étape 2 : deux côtés opposés, [EF] et [HG], sont à la fois parallèles et de même longueur : c'est une propriété caractéristique du parallélogramme. Étape 3 : avec le parallélisme seul, ce serait peut-être un trapèze, on ne pourrait pas conclure. Réponse : oui, EFGH est un parallélogramme, car deux côtés opposés sont parallèles et de même longueur">
  <style>@keyframes cp1122-pop { from { opacity: 0; } to { opacity: 1; } } .cp1122-pop { opacity: 0; animation: cp1122-pop 0.5s ease forwards; }</style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">On lit le codage, rien d'autre</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Une propriété caractéristique est-elle codée ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Un seul codage n'aurait pas suffi</text>
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${T(8, 44, 'Question : d\'après le codage, EFGH est-il un', '#1E3A8A')}${T(8, 58, 'parallélogramme ?', '#1E3A8A')}
    ${FIGURE}
    ${TICK(E, F)}${TICK(H, G)}
    ${T(84, 198, '(EF) // (HG)', '#1e293b', 700, 13)}
    ${T(232, 120, 'Codage lu :', '#1E3A8A')}${T(232, 134, 'un trait sur [EF] et', '#1E3A8A')}${T(232, 148, 'sur [HG] : EF = HG.', '#1E3A8A')}${T(232, 166, 'Mention : (EF) // (HG).', '#1E3A8A')}
  </g>
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif"><g class="cp1122-pop">
    ${SEG(E, F, '#1D9E75', 3.4)}${SEG(H, G, '#1D9E75', 3.4)}
    ${T(8, 226, '[EF] et [HG] sont deux côtés opposés, parallèles ET de même', '#1D9E75')}${T(8, 240, 'longueur : c\'est une propriété caractéristique du parallélogramme.', '#1D9E75')}
  </g></g>
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif"><g class="cp1122-pop">
    ${T(8, 266, 'Avec (EF) // (HG) seul, ce serait peut-être un trapèze :', '#B45309')}${T(8, 280, 'on ne pourrait pas conclure. L\'allure ne prouve rien, le codage si.', '#B45309')}
    ${T(8, 306, 'Réponse : oui, EFGH est un parallélogramme, car deux côtés', '#1e293b', 700, 11.5)}${T(8, 320, 'opposés sont parallèles et de même longueur.', '#1e293b', 700, 11.5)}
  </g></g>
</svg>`.trim();

const AIDE_CODAGE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Seuls les codages font foi.** Traits d'égalité, mention « parallèles », milieu des diagonales : on ne lit que cela. Une figure qui « a l'air » d'un parallélogramme ne prouve rien.",
                '**Ce qui suffit** pour affirmer qu\'un quadrilatère est un parallélogramme : ses côtés opposés parallèles deux à deux (la définition) ; ou ses côtés opposés de même longueur deux à deux ; ou ses diagonales qui se coupent en leur milieu ; ou deux côtés opposés à la fois parallèles et de même longueur.',
                '**La méthode** : je relis chaque codage, je cherche une des quatre propriétés complètes. Si elle y est, je conclus et je la cite ; sinon, le codage ne permet pas de conclure.',
            ],
        },
        { titre: 'Un exemple', svg: SVG_CODAGE },
    ],
};

const CP_1122 = {
    conclure_qcm_unique: AIDE_CODAGE,
    conclure_relier: AIDE_CODAGE,
    retrouver_qcm_unique: AIDE_CODAGE,
    vf_tableau_vf: AIDE_CODAGE,
};

export default CP_1122;
